import apiDefaults from '@/api/defaultConfiguration';
import { DamagecasesApiFactory } from '@/api/generated';
import { emptyCatch, getUnixTimestamp } from '@/plugins/Utils';

function isUpdateNeeded(payload, getters) {
	const id = payload?.id || getters.getDamageCase?.id;
	const isDamageCaseSet = getters.getDamageCase?.id !== undefined;
	const isDifferentId = isDamageCaseSet && payload.id && payload?.id.toString() !== getters.getDamageCase?.id.toString();
	const isDamageCaseOutdated = isDamageCaseSet && getters.getDamageCase?._ttl < getUnixTimestamp();
	return { id, isDamageCaseSet, isDifferentId, isDamageCaseOutdated };
}

function updateDamageCase(id, commit) {
	return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
		.damagecasesIdGet(id)
		.then(response => {
			if (response.data) {
				commit('setDamageCase', response.data);
			}
		})
		.catch(emptyCatch);
}

export default {
	state: {
		damageCase: null,
		damageCaseThumbnail: null,
		damageCaseWorkingImages: null,
		processCardsToggleStatus: {
			processDatasheet: true,
			overviewImage: true,
			damageAssessor: true,
			opoList: true,
			lawyerOffice: true,
			oppositeInsurance: true,
			accident: true,
			referenceNumber: true,
			customerConsultant: true,
			repairShop: true,
			repairExtensions: true,
			repairPlan: false,
			workingImages: true,
			repairProofs: true,
			note: true,
			approval: true,
		},
		rawUploadPictures: null,
	},
	getters: {
		isProcessLiability(state) {
			return state.damageCase?.process_type === 'liability';
		},
		isProcessOrderDocumentation(state) {
			return state.damageCase?.process_type === 'order_documentation';
		},
		isProcessComprehensive(state) {
			return state.damageCase?.process_type === 'comprehensive';
		},
		getProcessCardsToggleStatus(state) {
			return state.processCardsToggleStatus;
		},
		getDamageCase(state) {
			return state.damageCase;
		},
		getDamageCaseThumbnail(state) {
			return state.damageCaseThumbnail;
		},
		getDamageCaseWorkingImages(state) {
			return state.damageCaseWorkingImages;
		},
		getDamageCaseRepairShopLabel(state) {
			const isCarDealership = state.damageCase?.repairShop?.is_car_dealership;

			if (state.damageCase?.repairShop) {
				if (isCarDealership) {
					return 'Autohaus';
				} else {
					return 'Reparaturbetrieb';
				}
			} else {
				return 'Autohaus / Reparaturbetrieb';
			}
		},
		getRawUploadPictures(state) {
			return state.rawUploadPictures;
		},
	},
	mutations: {
		clearDamageCase(state) {
			state.damageCase = null;
		},
		clearDamageCaseWorkingImages(state) {
			state.damageCaseWorkingImages = null;
		},
		clearDamageCaseThumbnail(state) {
			state.damageCaseThumbnail = null;
		},
		setProcessCardsToggleStatus(state, payload) {
			state.state = payload;
		},
		setDamageCase(state, payload) {
			state.damageCase = payload;
			state.damageCase._ttl = getUnixTimestamp() + 60;
		},
		setDamageCaseThumbnail(state, payload) {
			state.damageCaseThumbnail = payload;
		},
		setDamageCaseWorkingImages(state, payload) {
			state.damageCaseWorkingImages = payload;
		},
		invalidateDamageCase(state, payload) {
			if (state.damageCase) {
				state.damageCase._ttl = 0;
			}
		},
		setRawPictures(state, payload) {
			state.rawUploadPictures = payload;
		},
	},
	actions: {
		forceUpdateDamageCase({ commit, dispatch, getters }, payload) {
			const id = payload?.id || getters.getDamageCase?.id;
			return updateDamageCase(id, commit);
		},
		updateDamageCase({ commit, dispatch, getters }, payload) {
			const { id, isDamageCaseSet, isDifferentId, isDamageCaseOutdated } = isUpdateNeeded(payload, getters);

			if (id && (!isDamageCaseSet || isDifferentId || isDamageCaseOutdated)) {
				if (isDifferentId || isDamageCaseOutdated) {
					commit('clearDamageCase');
					commit('clearDamageCaseWorkingImages');
					commit('clearDamageCaseThumbnail');
					commit('clearRepairProof');
					commit('clearRepairExtension');
				}
				return updateDamageCase(id, commit);
			}
		},
		updateDamageCaseThumbnail({ commit, dispatch, getters }, payload) {
			const { id, isDamageCaseSet, isDifferentId, isDamageCaseOutdated } = isUpdateNeeded(payload, getters);
			const isThumbnailSet = getters.getDamageCaseThumbnail;

			if (id && (!isDamageCaseSet || isDifferentId || isDamageCaseOutdated || !isThumbnailSet)) {
				return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.damagecasesIdImageTypeGet(id, 'thumbnail')
					.then(response => {
						if (response.data) {
							commit('setDamageCaseThumbnail', response.data);
						}
					})
					.catch(error => commit('setDamageCaseThumbnail', {}));
			}
		},
		updateDamageCaseWorkingImages({ commit, dispatch, getters }, payload) {
			const { id, isDamageCaseSet, isDifferentId, isDamageCaseOutdated } = isUpdateNeeded(payload, getters);
			const isWorkingImagesSet = getters.getDamageCaseWorkingImages?.length;

			if (id && (!isDamageCaseSet || isDifferentId || isDamageCaseOutdated || !isWorkingImagesSet)) {
				return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.damagecasesIdImageTypeGet(id, 'workingimages')
					.then(response => {
						if (response.data) {
							commit('setDamageCaseWorkingImages', response.data);
						}
					})
					.catch(emptyCatch);
			}
		},
	},
};
